import _ from 'lodash';

import { ServiceClient } from '@services/coralservices/ServiceClient';
import { EnvironmentDetail } from '@utils/EnvironmentDetailUtils';

/**
 * Calls UserPreferenceManagementService (UPS) for the platform-level display
 * `language` preference (toolId=PLATFORM, page=GLOBAL, qualifier HHU/DESKTOP).
 *
 * Drives UPS through the existing ServiceClient relay (awsJson1_0) via a minimal
 * coral shim. The endpoint stage is resolved from EnvironmentDetail.getDomain();
 * EU has no beta deployment and falls back to NA. Gated to non-prod.
 */

export type UpsStage = 'beta' | 'gamma' | 'prod';

const UPS_NA_BY_STAGE: Record<UpsStage, string> = {
    beta: 'https://us-west-2-beta.user-preferences.wfm.amazon.dev',
    gamma: 'https://us-west-2-gamma.user-preferences.wfm.amazon.dev',
    prod: 'https://us-east-1-prod.user-preferences.wfm.amazon.dev',
};

const UPS_EU_BY_STAGE: Record<UpsStage, string> = {
    beta: '',
    gamma: 'https://eu-south-2-gamma-zaz.user-preferences.wfm.amazon.dev',
    prod: 'https://eu-south-2-prod-zaz.user-preferences.wfm.amazon.dev',
};

/** Maps the ATBase domain (test/gamma/prod) to a UPS stage. Unknown -> beta. */
export const detectUpsStage = (domain: string | null | undefined): UpsStage => {
    if (domain === 'prod') {
        return 'prod';
    }
    if (domain === 'gamma') {
        return 'gamma';
    }
    return 'beta';
};

/**
 * Resolves the NA/EU endpoints for a stage. EU falls back to NA where there is
 * no EU deployment for the stage (beta).
 */
export const resolveUpsEndpoints = (stage: UpsStage): { na: string; eu: string } => {
    const na = UPS_NA_BY_STAGE[stage];
    return { na, eu: UPS_EU_BY_STAGE[stage] || na };
};

const UPS_OPERATION_PREFIX = 'UserPreferenceManagementService';

const PLATFORM_TOOL_ID = 'PLATFORM';
const GLOBAL_PAGE = 'GLOBAL';
const LANGUAGE_KEY = 'language';

// Hard cap on the bootstrap read so it never delays login.
const GET_TIMEOUT_MS = 1000;
// PutUserPreferences retry policy (1 initial + retries).
const PUT_MAX_RETRIES = 2;
const PUT_BASE_DELAY_MS = 200;

// EU/EEA country codes routed to the EU deployment (eu-south-2 / ZAZ).
// Associates in these countries authenticate via Cognito pool eu-west-1_m8q62lIt0
// whose signing keys are trusted only by EU UPMS, not US UPMS.
// Source: ISO 3166-1 alpha-2 for EU27 + EEA + UK.
// prettier-ignore
const EU_COUNTRY_CODES = [
  'GB', 'UK', 'IE', 'DE', 'FR', 'IT', 'ES',
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK',
  'EE', 'FI', 'GR', 'HU', 'LV', 'LT', 'LU',
  'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'SE',
  'NO', 'LI', 'IS'
];

/**
 * Minimal "coral client" the ServiceClient relay understands:
 *  - `post` own-property so ServiceClient.getClient() returns the shim as-is.
 *  - `config` carries endpoint()/operationPrefix() plus the no-op realm/domain overrides.
 */
const buildCoralClientShim = (endpoint: string) => ({
    post: true,
    config: {
        endpoint: () => endpoint,
        operationPrefix: () => UPS_OPERATION_PREFIX,
        overrideRealm: (_realm: string) => undefined,
        overrideDomain: (_domain: string) => undefined,
    },
});

function safeGetDomain(): string | null | undefined {
    try {
        return EnvironmentDetail.getDomain();
    } catch {
        return undefined;
    }
}

function resolveDefaultQualifier(): string {
    // UPS qualifier contract is 'HHU' | 'DESKTOP' (device-based, independent of auth),
    // matching SpaceDockRelay's QualifierResolver. ATBase detects handheld via getDeviceType().
    try {
        return EnvironmentDetail.getDeviceType() === 'handheld' ? 'HHU' : 'DESKTOP';
    } catch {
        return 'DESKTOP';
    }
}

// Stage, endpoints, clients and the default qualifier are resolved once at module
// load — the domain and device type are fixed for the lifetime of the session.
const UPS_STAGE: UpsStage = detectUpsStage(safeGetDomain());
const { na: UPS_NA_ENDPOINT, eu: UPS_EU_ENDPOINT } = resolveUpsEndpoints(UPS_STAGE);
const NA_CLIENT = new ServiceClient(UPS_OPERATION_PREFIX, buildCoralClientShim(UPS_NA_ENDPOINT));
const EU_CLIENT = new ServiceClient(UPS_OPERATION_PREFIX, buildCoralClientShim(UPS_EU_ENDPOINT));
const DEFAULT_QUALIFIER = resolveDefaultQualifier();

/** Minimal shape of the UPS GetUserPreferences response we read. */
interface IGetUserPreferencesResponse {
    preferences?: unknown;
}

export class UserPreferencesServiceClient {
    /**
     * Reads the stored global display language for the user.
     * Resolves to the stored language code, or `null` when absent / on timeout / error.
     * Never throws — callers fall back to their own default (e.g. store-country locale).
     */
    public static async getLanguage(userId: string, storeId: string, countryCode?: string | null): Promise<string | null> {
        if (UserPreferencesServiceClient.isProdEnvironment()) {
            return null;
        }
        try {
            const request = UserPreferencesServiceClient.getClientForCountry(countryCode).sendRequest(
                {
                    operation: 'GetUserPreferences',
                    data: {
                        toolId: PLATFORM_TOOL_ID,
                        userId,
                        qualifier: DEFAULT_QUALIFIER,
                    },
                },
                storeId,
            );
            // If the timeout wins the race, nothing else awaits `request`; the relay can't
            // abort it, so attach a no-op catch to ensure a late rejection is observed and
            // never surfaces as an unhandled promise rejection. (The race below still
            // observes the rejection when the request loses normally.)
            request.catch(() => undefined);

            // Cap the wait with an AbortSignal-driven timeout. NOTE: the request rides a
            // cross-frame relay that cannot honour an AbortSignal, so on timeout we stop
            // awaiting but the underlying request may still settle in the background.
            const controller = new AbortController();
            const timerId = setTimeout(() => controller.abort(), GET_TIMEOUT_MS);
            const timeout = new Promise<never>((_resolve, reject) => {
                controller.signal.addEventListener('abort', () => reject(new Error('UPS_GET_TIMEOUT')));
            });

            try {
                const response = (await Promise.race([request, timeout])) as IGetUserPreferencesResponse;
                return UserPreferencesServiceClient.extractLanguage(response?.preferences);
            } finally {
                clearTimeout(timerId);
            }
        } catch (error) {
            console.warn('ATBase: UserPreferenceManagementService.GetUserPreferences failed; using default locale', {
                reason: UserPreferencesServiceClient.midwayUnsupportedReason(),
                ...UserPreferencesServiceClient.describeError(error),
            });
            return null;
        }
    }

    /**
     * Persists the user's global display language (page=GLOBAL).
     * Fire-and-forget with bounded retries; never throws. Retries only transient
     * (network / 5xx) failures — a client fault (4xx) is terminal.
     */
    public static setLanguage(userId: string, storeId: string, language: string, countryCode?: string | null): Promise<void> {
        if (UserPreferencesServiceClient.isProdEnvironment()) {
            return Promise.resolve();
        }
        return new Promise<void>((resolve) => {
            const attempt = (n: number): void => {
                UserPreferencesServiceClient.getClientForCountry(countryCode)
                    .sendRequest(
                        {
                            operation: 'PutUserPreferences',
                            data: {
                                toolId: PLATFORM_TOOL_ID,
                                userId,
                                qualifier: DEFAULT_QUALIFIER,
                                page: GLOBAL_PAGE,
                                // Model: `preferences` is a JSON-encoded string (clients must stringify).
                                preferences: JSON.stringify({ [LANGUAGE_KEY]: language }),
                            },
                        },
                        storeId,
                    )
                    .then(() => resolve())
                    .catch((error: unknown) => {
                        // 4xx never succeeds on retry; stop immediately. Otherwise schedule the
                        // next attempt with exponential backoff (self-referential setTimeout, no sleep).
                        if (UserPreferencesServiceClient.isClientFault(error) || n === PUT_MAX_RETRIES) {
                            console.warn('ATBase: UserPreferenceManagementService.PutUserPreferences failed; preference not saved', {
                                reason: UserPreferencesServiceClient.midwayUnsupportedReason(),
                                ...UserPreferencesServiceClient.describeError(error),
                            });
                            resolve();
                            return;
                        }
                        setTimeout(() => attempt(n + 1), PUT_BASE_DELAY_MS * 2 ** n);
                    });
            };
            attempt(0);
        });
    }

    /**
     * Feature gate: UPS language persistence is enabled only in non-prod while the
     * integration is being validated. Treated as prod if EITHER the live domain or
     * the resolved UPS stage indicates prod. Fails CLOSED — if the environment can't
     * be determined (getDomain throws), we assume prod so the feature stays gated off
     * and never emits unintended network traffic from a prod deployment.
     */
    private static isProdEnvironment(): boolean {
        try {
            return EnvironmentDetail.getDomain() === 'prod' || UPS_STAGE === 'prod';
        } catch {
            return true;
        }
    }

    /**
     * Explains why the call fails on Midway/Desktop right now (returned for logging).
     * ServiceClient routes by auth context: IIB (HHU) uses the config path our shim
     * supports; Midway/Desktop uses SentryService.client.post(), which the shim does
     * not implement. Full Desktop support needs the generated UPS coral JS client.
     * Returns undefined on IIB (no issue) or when the context can't be determined.
     */
    private static midwayUnsupportedReason(): string | undefined {
        try {
            if (EnvironmentDetail.isIIBAuthN()) {
                return undefined;
            }
            return 'Midway/Desktop routes through SentryService.client.post(), which the minimal UPS coral shim does not implement (only the IIB/config path is supported). Needs the generated UPS coral JS client; until then test on IIB/HHU (or load with ?auth=iib).';
        } catch {
            return undefined;
        }
    }

    /** UK / EU stores use the EU client; everything else uses NA. Both are built once. */
    private static getClientForCountry(countryCode?: string | null): ServiceClient {
        return UserPreferencesServiceClient.isEUCountry(countryCode) ? EU_CLIENT : NA_CLIENT;
    }

    /** A client fault (4xx / validation / auth) will not succeed on retry. */
    private static isClientFault(error: unknown): boolean {
        const e = error as { statusCode?: number; metadata?: { statusCode?: number }; name?: string; data?: { __type?: string } } | null | undefined;
        const status = e?.metadata?.statusCode ?? e?.statusCode;
        if (typeof status === 'number' && status >= 400 && status < 500) {
            return true;
        }
        const clientFaultTypes = new Set(['ValidationException', 'NotAuthorizedException', 'AccessDeniedException']);
        if (e?.name && clientFaultTypes.has(e.name)) {
            return true;
        }
        return !!(e?.data?.__type && clientFaultTypes.has(e.data.__type));
    }

    /** Normalises any thrown value into loggable fields (name/message/stack). */
    private static describeError(error: unknown): { name?: string; message?: string; stack?: string } {
        if (error instanceof Error) {
            return { name: error.name, message: error.message, stack: error.stack };
        }
        return { message: String(error) };
    }

    /**
     * Extracts the GLOBAL/language value from a UPS GetUserPreferences response.
     * The model returns `preferences` as a JSON-encoded string of the nested page
     * map (absent for first-time users); tolerate an already-parsed object too.
     * Returns null on absent / malformed / empty values.
     */
    private static extractLanguage(rawPreferences: unknown): string | null {
        if (rawPreferences == null) {
            return null;
        }
        let pages: unknown = rawPreferences;
        if (_.isString(rawPreferences)) {
            if (rawPreferences.trim() === '') {
                return null;
            }
            try {
                pages = JSON.parse(rawPreferences);
            } catch {
                return null;
            }
        }
        const language = (pages as Record<string, Record<string, unknown> | undefined> | null | undefined)?.[GLOBAL_PAGE]?.[LANGUAGE_KEY];
        return _.isString(language) && language.trim() !== '' ? language : null;
    }

    private static isEUCountry(countryCode?: string | null): boolean {
        if (!countryCode) {
            return false;
        }
        return EU_COUNTRY_CODES.includes(countryCode.toUpperCase());
    }
}
